import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSeoMeta } from '@unhead/react';
import { ArrowDownWideNarrow, ArrowUpNarrowWide, Plus } from 'lucide-react';
import { SiteFooter, SiteHeader } from '@/components/SiteHeader';
import { PackCard, PackCardSkeleton } from '@/components/PackCard';
import { useFollowPacks, PACK_PAGE_SIZES, type PackFilter } from '@/hooks/useFollowPacks';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useMyFollowsQuery } from '@/hooks/useFollowActions';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import type { FollowPack } from '@/lib/followPacks';

const FILTER_OPTIONS: { value: PackFilter; label: string }[] = [
  { value: 'all', label: 'Show all packs' },
  { value: 'following', label: 'From people I follow' },
  { value: 'included', label: "Packs I'm in" },
  { value: 'mine', label: 'My packs' },
];

type SortKey = 'updated' | 'members' | 'az';

const SORT_OPTIONS: { value: SortKey; label: string }[] = [
  { value: 'updated', label: 'Recently updated' },
  { value: 'members', label: 'Members' },
  { value: 'az', label: 'A to Z' },
];

const DIRECTION_LABEL: Record<SortKey, { asc: string; desc: string }> = {
  updated: { asc: 'Oldest updated first', desc: 'Recently updated first' },
  members: { asc: 'Fewest members first', desc: 'Most members first' },
  az: { asc: 'A to Z', desc: 'Z to A' },
};

function sortPacks(packs: FollowPack[], sort: SortKey, ascending: boolean): FollowPack[] {
  const sorted = [...packs];
  switch (sort) {
    case 'members':
      sorted.sort((a, b) =>
        ascending
          ? a.members.length - b.members.length || b.createdAt - a.createdAt
          : b.members.length - a.members.length || b.createdAt - a.createdAt,
      );
      break;
    case 'az': {
      const cmp = (a: FollowPack, b: FollowPack) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }) || b.createdAt - a.createdAt;
      sorted.sort((a, b) => (ascending ? cmp(a, b) : cmp(b, a)));
      break;
    }
    case 'updated':
    default:
      // Ties broken by name so pages look stable.
      sorted.sort((a, b) =>
        ascending
          ? a.createdAt - b.createdAt || a.name.localeCompare(b.name)
          : b.createdAt - a.createdAt || a.name.localeCompare(b.name),
      );
      break;
  }
  return sorted;
}

export default function Index() {
  useSeoMeta({
    title: 'Follow Party — Nostr Follow Packs',
    description: 'Discover and share curated follow packs for Nostr. Find interesting people or publish your own pack.',
  });

  const { user } = useCurrentUser();
  const [filter, setFilter] = useState<PackFilter>('all');
  const [sort, setSort] = useState<SortKey>('updated');
  // Direction per sort: updated starts newest-first, members most-first, az A-to-Z.
  const [ascending, setAscending] = useState<Record<SortKey, boolean>>({
    updated: false,
    members: false,
    az: true,
  });
  const [until, setUntil] = useState<number | undefined>(undefined);
  const [accumulated, setAccumulated] = useState<FollowPack[]>([]);
  const [pageSize, setPageSize] = useState<number>(PACK_PAGE_SIZES[0]);
  // Visual feedback state for the Load-next buttons.
  const [pressedSize, setPressedSize] = useState<number | null>(null);
  const [flashKey, setFlashKey] = useState(0);
  const [flashOn, setFlashOn] = useState(false);

  const activeFilter: PackFilter = user ? filter : 'all';
  const { data: myFollows } = useMyFollowsQuery(Boolean(user) && activeFilter === 'following');

  const followingList = useMemo(() => {
    if (!user) return undefined;
    const base = myFollows ?? [];
    return base.includes(user.pubkey) ? base : [...base, user.pubkey];
  }, [myFollows, user]);

  const filterReady = activeFilter !== 'following' || Boolean(followingList);

  const { data, isLoading, isError } = useFollowPacks({
    filter: activeFilter,
    userPubkey: user?.pubkey,
    following: activeFilter === 'following' ? followingList : undefined,
    until,
    limit: pageSize,
    enabled: filterReady,
  });

  // Accumulate pages, de-duplicated by author+id. Sort/filter changes never
  // touch this cache — they only re-order what's already loaded.
  useEffect(() => {
    if (!data) return;
    setAccumulated((prev) => {
      if (prev.length === 0) return data.packs;
      const known = new Set(prev.map((p) => `${p.pubkey}:${p.id}`));
      const added = data.packs.filter((p) => !known.has(`${p.pubkey}:${p.id}`));
      if (added.length === 0) return prev;
      // Flash the "showing" counter green on every successful load.
      setFlashKey((k) => k + 1);
      return [...prev, ...added];
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const changeFilter = (v: PackFilter) => {
    setFilter(v);
    setUntil(undefined);
    setAccumulated([]);
  };

  /** Incremental loader: each click fetches `size` older packs and appends them. Never shrinks. */
  const loadNext = (size: number) => {
    setPressedSize(size);
    setPageSize(size);
    // Paginate from the current oldest cursor so new packs append to the list.
    if (oldest) setUntil(oldest);
  };

  const changeSort = (v: SortKey) => {
    setSort(v);
    // Pure client-side re-sort: loaded packs stay exactly as they are.
  };

  const toggleDirection = () => {
    setAscending((prev) => ({ ...prev, [sort]: !prev[sort] }));
  };

  const sortAsc = ascending[sort];
  const directionHint = sortAsc ? DIRECTION_LABEL[sort].asc : DIRECTION_LABEL[sort].desc;

  // Single source of truth for "everything loaded so far". TanStack Query's
  // `data` only holds the latest page — this cache holds ALL pages merged.
  // Sorting only re-orders this array; it never re-fetches or truncates.
  const rawPacks = accumulated.length > 0 ? accumulated : (data?.packs ?? []);
  const packs = useMemo(() => sortPacks(rawPacks, sort, sortAsc), [rawPacks, sort, sortAsc]);
  // Pagination cursor must come from relay order (newest→oldest), NOT from the
  // currently sorted view — otherwise flipping a sort moves the cursor and the
  // counter appears to reset.
  const oldest = useMemo(() => {
    if (rawPacks.length === 0) return undefined;
    let min = rawPacks[0].createdAt;
    for (let i = 1; i < rawPacks.length; i++) {
      if (rawPacks[i].createdAt < min) min = rawPacks[i].createdAt;
    }
    return min;
  }, [rawPacks]);

  // Green flash on the "showing" counter: turn on when new packs land, fade off after ~1.2s.
  useEffect(() => {
    if (flashKey === 0) return;
    setFlashOn(true);
    const t = window.setTimeout(() => setFlashOn(false), 1200);
    return () => window.clearTimeout(t);
  }, [flashKey]);
  const [scrollFade, setScrollFade] = useState(0);

  useEffect(() => {
    const onScroll = () => {
      // Fade + dim the banner as it scrolls out of view (0 → 1).
      const y = window.scrollY;
      setScrollFade(Math.min(1, y / 420));
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />
      <style>{`@keyframes hero-drift { from { transform: scale(1); } to { transform: scale(1.06); } }`}</style>
      <main className="flex-1">
        <section>
          {/* Campfire banner art — full clarity up top, scrolls away + fades as you scroll */}
          <div className="relative overflow-hidden bg-[#0b1026]">
            <img
              src="/hero-banner.png"
              alt="Four adventurers around a campfire under a starry sky with Follow Party in the stars"
              className="mx-auto h-64 w-full object-cover object-center motion-safe:[animation:hero-drift_24s_ease-in-out_infinite_alternate] sm:h-80 sm:object-contain lg:h-[26rem] lg:object-contain xl:h-[30rem]"
              style={
                typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
                  ? undefined
                  : { transform: `scale(${1 + scrollFade * 0.04})`, opacity: 1 - scrollFade * 0.55 }
              }
            />
            {/* Bottom edge melts the art into the page */}
            <div
              aria-hidden
              className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-b from-transparent to-background"
            />
          </div>
          <div className="container py-10 text-center sm:py-14">
            <p className="mx-auto inline-flex items-center gap-2 rounded-full border bg-card px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">
              <span className="size-2 rounded-full bg-accent" aria-hidden />
              Curated follows, minus the doomscroll
            </p>
            <h1 className="mx-auto mt-5 max-w-3xl text-5xl leading-[1.02] tracking-tight sm:text-7xl">
              Find your party
            </h1>
            <p className="mx-auto mt-5 max-w-2xl text-lg text-muted-foreground sm:text-xl">
              Follow packs are shareable lists of Nostr users with similar interests. Join the party — or create your own.
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
              <Button asChild size="lg" className="rounded-full px-7">
                <Link to="/create">
                  <Plus className="size-4" />
                  Create a pack
                </Link>
              </Button>
            </div>
            {!user ? (
              <p className="mt-4 text-sm text-muted-foreground">
                Log in to create packs and use filters.
              </p>
            ) : null}
          </div>
        </section>

        <section id="packs" className="container scroll-mt-20 pb-20">
          <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
            <h2 className="font-display text-3xl tracking-tight">
              {sort === 'updated' ? (sortAsc ? 'Oldest packs' : 'Recently updated') : sort === 'members' ? 'Packs by size' : 'Packs A–Z'}
            </h2>
            <div className="flex flex-wrap items-center gap-2">
              <Select value={sort} onValueChange={(v) => changeSort(v as SortKey)}>
                <SelectTrigger className="w-52 rounded-full" aria-label="Sort packs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SORT_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                size="icon"
                className="rounded-full"
                onClick={toggleDirection}
                aria-label={`${directionHint} — flip order`}
                title={`${directionHint} — click to flip`}
              >
                {sortAsc ? <ArrowUpNarrowWide className="size-4" /> : <ArrowDownWideNarrow className="size-4" />}
              </Button>
              {user ? (
                <Select value={activeFilter} onValueChange={(v) => changeFilter(v as PackFilter)}>
                  <SelectTrigger className="w-52 rounded-full" aria-label="Filter packs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FILTER_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : null}
            </div>
          </div>

          {/* Load-next shortcuts: each click appends that many older packs (never shrinks) */}
          <div className="mb-5 flex flex-wrap items-center gap-2">
            <span className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
              Load next
            </span>
            <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Load more packs">
              {PACK_PAGE_SIZES.map((size) => {
                const isPressed = pressedSize === size && isLoading;
                return (
                  <Button
                    key={size}
                    variant={isPressed ? 'default' : 'outline'}
                    size="sm"
                    className="rounded-full transition-all motion-safe:active:scale-95"
                    disabled={isLoading || (!data?.hasMore && packs.length > 0)}
                    onClick={() => loadNext(size)}
                    title={`Load the next ${size} packs`}
                    aria-live="polite"
                  >
                    {isPressed ? (
                      <span className="inline-flex items-center gap-1.5">
                        <span className="size-3 animate-spin rounded-full border-2 border-current border-t-transparent" aria-hidden />
                        +{size}
                      </span>
                    ) : (
                      `+${size}`
                    )}
                  </Button>
                );
              })}
            </div>
            <span
              key={flashKey}
              aria-live="polite"
              className={`text-xs font-medium motion-safe:transition-colors motion-safe:duration-1000 ${
                flashOn ? 'text-green-600 dark:text-green-400' : 'text-muted-foreground'
              }`}
            >
              showing {packs.length}{data?.hasMore ? '+' : ''}
            </span>
          </div>

          {isLoading && packs.length === 0 ? (
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <PackCardSkeleton key={i} />
              ))}
            </div>
          ) : isError ? (
            <Card className="border-dashed">
              <CardContent className="px-8 py-12 text-center">
                <p className="mx-auto max-w-sm text-muted-foreground">
                  Couldn't reach relays. Check your connection and try again in a moment.
                </p>
              </CardContent>
            </Card>
          ) : packs.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="px-8 py-12 text-center">
                <p className="mx-auto max-w-sm text-muted-foreground">
                  No packs found yet. Be the first to create one!
                </p>
                <Button asChild className="mt-4 rounded-full">
                  <Link to="/create">
                    <Plus className="size-4" />
                    Create a pack
                  </Link>
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {packs.map((pack) => (
                <PackCard key={`${pack.pubkey}:${pack.id}`} pack={pack} />
              ))}
            </div>
          )}

          {data?.hasMore && oldest ? (
            <div className="mt-10 text-center">
              <Button
                variant="outline"
                className="rounded-full px-6"
                disabled={isLoading}
                onClick={() => setUntil(oldest)}
              >
                {isLoading ? 'Loading…' : 'Discover more'}
              </Button>
            </div>
          ) : null}
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
